import React, { useEffect, useState } from "react";
import { PageHeader, Modal, Toast } from "@/components/Layout";
import api from "@/lib/api";
import Select from "@/components/Select";
import ViewOnly from "@/components/ViewOnly";
import { useAuth } from "@/context/AuthContext";
import { lock } from "@/lib/perm";
import RowActions from "@/components/RowActions";

const ENTITY_LABEL = { person: "Angajat", employment: "Raport de muncă" };
const EMPTY = { entity: "person", label: "", field_type: "text", unit: "", options: [], optionsText: "", required: false, sensitive: false, section: "", sort_order: 0, active: true };

// Câmpurile personalizate ale clientului: definite aici, completate în fișa Angajatului (și, pentru câmpurile de pe
// „Raport de muncă”, în raportul de muncă). Un câmp care are valori nu se șterge, se dezactivează.
export default function CustomFieldDefs() {
  const { has } = useAuth();
  const [rows, setRows] = useState([]);
  const [types, setTypes] = useState([]);
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [msg, setMsg] = useState({ text: "", type: "info" });

  const load = () => api.get("/custom-fields", { params: { include_inactive: true } }).then(r => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/custom-fields/types").then(r => setTypes(r.data)).catch(() => {}); }, []);
  const flash = (text, type = "success") => { setMsg({ text, type }); setTimeout(() => setMsg({ text: "" }), 2500); };
  const fail = (e) => setMsg({ text: e?.response?.data?.detail || "Eroare", type: "error" });

  const save = async () => {
    if (!editing.label.trim()) return setMsg({ text: "Eticheta este obligatorie", type: "error" });
    const isList = ["select", "multiselect"].includes(editing.field_type);
    const body = { ...editing, options: isList ? editing.optionsText.split("\n").map(s => s.trim()).filter(Boolean) : [] };
    delete body.optionsText;
    try {
      if (editing.id) await api.put(`/custom-fields/${editing.id}`, body); else await api.post("/custom-fields", body);
      setEditing(null); load(); flash("Salvat");
    } catch (e) { fail(e); }
  };
  const remove = async () => {
    try { await api.delete(`/custom-fields/${confirmDel.id}`); setConfirmDel(null); load(); flash("Câmp șters"); }
    catch (e) { setConfirmDel(null); fail(e); }
  };
  const typeLabel = (t) => types.find(x => x.value === t)?.label || t;
  const isList = ["select", "multiselect"].includes(editing?.field_type);

  return (
    <>
      <PageHeader title="Câmpuri personalizate" subtitle="Câmpuri definite de organizația ta, pe Angajat și pe Raportul de muncă"
        actions={<button className="btn btn-primary" data-testid="add-cfdef" onClick={() => setEditing({ ...EMPTY })} {...lock(has("custom_fields.manage"))}>+ Câmp nou</button>} />
      <Toast msg={msg.text} type={msg.type} />
      <div className="card" style={{ overflowX: "auto" }}>
        <table className="tbl" data-testid="cfdef-table">
          <thead><tr><th>Etichetă</th><th>Se completează la</th><th>Tip</th><th>Secțiune</th><th>Obligatoriu</th><th>Sensibil</th><th>Stare</th><th></th></tr></thead>
          <tbody>{rows.map(r => (
            <tr key={r.id} data-testid={`cfdef-row-${r.key}`} style={r.active ? undefined : { opacity: 0.55 }}>
              <td style={{ fontWeight: 600 }}>{r.label}{r.unit ? ` (${r.unit})` : ""}</td>
              <td>{ENTITY_LABEL[r.entity]}</td>
              <td>{typeLabel(r.field_type)}{r.options?.length ? `: ${r.options.join(", ")}` : ""}</td>
              <td>{r.section || "—"}</td>
              <td>{r.required ? "Da" : "Nu"}</td>
              <td>{r.sensitive ? "Da 🔒" : "Nu"}</td>
              <td>{r.active ? "Activ" : "Dezactivat"}</td>
              <td>
                <RowActions testid={`row-menu-${r.key}`} items={[
                  { label: "Vizualizează", icon: "eye", testid: `cfdef-view-${r.key}`, onClick: () => setEditing({ ...r, optionsText: (r.options || []).join("\n"), view: true }) },
                  { label: "Editează", icon: "edit", testid: `cfdef-edit-${r.key}`, onClick: () => setEditing({ ...r, optionsText: (r.options || []).join("\n") }), ...lock(has("custom_fields.manage")) },
                  { label: "Șterge", icon: "close", danger: true, testid: `cfdef-del-${r.key}`, onClick: () => setConfirmDel(r), ...lock(has("custom_fields.manage")) },
                ]} />
              </td>
            </tr>))}
            {rows.length === 0 && <tr><td colSpan={8} style={{ textAlign: "center", color: "#94A3B8", padding: 24 }}>Niciun câmp personalizat definit. Folosește „+ Câmp nou”.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && (
        <Modal title={editing.view ? "Câmp personalizat" : editing.id ? "Editează câmp personalizat" : "Câmp personalizat nou"} onClose={() => setEditing(null)} width={640}>
          <ViewOnly on={editing.view}>
            <div className="grid-2">
              <div><label className="label label-req">Etichetă</label>
                <input className="input" value={editing.label} onChange={e => setEditing({ ...editing, label: e.target.value })} data-testid="cfd-label" maxLength={128} /></div>
              <div><label className="label">Se completează la</label>
                <Select className="select" value={editing.entity} disabled={!!editing.id} onChange={e => setEditing({ ...editing, entity: e.target.value })} data-testid="cfd-entity">
                  <option value="person">Angajat (se poate trimite spre rapoartele lui de muncă)</option>
                  <option value="employment">Raport de muncă</option>
                </Select></div>
              <div><label className="label">Tip</label>
                <Select className="select" value={editing.field_type} disabled={!!editing.id} onChange={e => setEditing({ ...editing, field_type: e.target.value })} data-testid="cfd-type">
                  {types.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </Select></div>
              <div><label className="label">Unitate de măsură</label>
                <input className="input" value={editing.unit} onChange={e => setEditing({ ...editing, unit: e.target.value })} placeholder="ex: număr, kg, cm" data-testid="cfd-unit" maxLength={32} /></div>
              {isList && (
                <div style={{ gridColumn: "1/-1" }}><label className="label label-req">Opțiuni (câte una pe rând)</label>
                  <textarea className="input" rows={4} value={editing.optionsText} onChange={e => setEditing({ ...editing, optionsText: e.target.value })} data-testid="cfd-options" /></div>)}
              <div><label className="label">Secțiune (grupare în fișă)</label>
                <input className="input" value={editing.section} onChange={e => setEditing({ ...editing, section: e.target.value })} data-testid="cfd-section" maxLength={64} /></div>
              <div><label className="label">Ordine</label>
                <input className="input" type="number" value={editing.sort_order} onChange={e => setEditing({ ...editing, sort_order: parseInt(e.target.value || "0", 10) })} data-testid="cfd-order" /></div>
              <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13 }}>
                <input type="checkbox" checked={editing.required} onChange={e => setEditing({ ...editing, required: e.target.checked })} data-testid="cfd-required" />Obligatoriu</label>
              <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13 }}>
                <input type="checkbox" checked={editing.sensitive} onChange={e => setEditing({ ...editing, sensitive: e.target.checked })} data-testid="cfd-sensitive" />Sensibil (criptat, vizibil doar cu dreptul pentru date sensibile)</label>
              <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13 }}>
                <input type="checkbox" checked={editing.active} onChange={e => setEditing({ ...editing, active: e.target.checked })} data-testid="cfd-active" />Activ</label>
            </div>
          </ViewOnly>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
            <button className="btn btn-secondary" onClick={() => setEditing(null)}>{editing.view ? "Închide" : "Anulează"}</button>
            {!editing.view && <button className="btn btn-primary" onClick={save} data-testid="cfd-save">Salvează</button>}
          </div>
        </Modal>
      )}
      {confirmDel && (
        <Modal title="Șterge câmpul" onClose={() => setConfirmDel(null)} width={460}>
          <div style={{ fontSize: 13 }}>Ștergi câmpul <b>{confirmDel.label}</b>? Un câmp care are deja valori nu se poate șterge; dezactivează-l din „Editează”.</div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 14 }}>
            <button className="btn btn-secondary" onClick={() => setConfirmDel(null)}>Anulează</button>
            <button className="btn btn-danger" onClick={remove} data-testid="cfdef-del-confirm">Șterge</button>
          </div>
        </Modal>
      )}
    </>
  );
}
